import ScrollReveal from "@/components/base/ScrollReveal";
import { Link } from "react-router-dom";

export default function ServicesSection() {
  const services = [
    {
      icon: "ri-compass-3-line",
      title: "AI Strategy Development",
      desc: "Clear AI strategy starts with understanding where the organization is today, where it wants to go, and what barriers must be addressed. VantageSight helps leaders define practical AI aspirations, goals, priorities, roadmaps, and implementation plans that are grounded in organizational readiness and stakeholder input.",
      leadIn: "VantageSight guides, advises, and accelerates leaders and teams in efforts such as:",
      bullets: [
        "Enterprise and organization-specific AI strategy development",
        "AI aspiration, goals, and objectives definition",
        "AI roadmap development",
        "Strategy implementation planning",
        "Stakeholder alignment",
        "Quality review of existing AI plans",
      ],
    },
    {
      icon: "ri-shield-check-line",
      title: "AI Governance",
      desc: "VantageSight helps organizations establish practical, proportionate AI governance so AI use can grow with confidence, consistency, and appropriate oversight and comply with relevant regulations and standards (such as U.S. OMB Memos, the EU AI Act, U.S. state laws, ISO/IEC 42001, etc.).",
      leadIn: "VantageSight's guidance accelerates leaders and teams to establish effective AI governance practices such as:",
      bullets: [
        "Trustworthy AI principles aligned with relevant standards/directives",
        "AI use case inventory approach and use case reviews",
        "AI governance processes and review points aligned with the AI lifecycle",
        "Regulations, standards, and guidance gap analysis and compliance",
        "AI use case risk management approach",
        "Clear governance roles, responsibilities, and ownership",
        "Internal AI policies",
      ],
      featured: true,
    },
    {
      icon: "ri-team-line",
      title: "AI Workforce Readiness",
      desc: "Successful AI adoption depends on leaders and employees being ready to work with AI in a clear, confident, and responsible way. VantageSight helps organizations assess workforce readiness, identify workforce implications, and plan for the roles, knowledge, skills, and abilities needed for AI-enabled work. VantageSight also provides clarity about what types of AI-related organizational structures (groups, teams, community of practice, center of excellence, etc.) may make the most sense for the enterprise.",
      leadIn: "VantageSight focuses and guides leaders and their teams in activities such as:",
      bullets: [
        "Workforce AI readiness assessment",
        "Leader AI readiness considerations",
        "AI roles and skills planning",
        "Training strategy development",
        "Change readiness",
      ],
    },
    {
      icon: "ri-rocket-line",
      title: "AI Adoption",
      desc: "AI adoption is not just a technology challenge. It requires clear goals, stakeholder involvement, business alignment, communication, governance, and ongoing review. VantageSight helps organizations create practical adoption approaches that reduce confusion and increase the likelihood of successful AI use.",
      leadIn: "VantageSight advises organizations in activities such as:",
      bullets: [
        "AI adoption planning",
        "Stakeholder engagement",
        "Adoption barriers and enablers identification and mitigation",
        "Roadmap creation and implementation",
        "Communication and alignment across the enterprise",
      ],
    },
    {
      icon: "ri-slideshow-line",
      title: "Workshops & Assessments",
      desc: "VantageSight helps leaders and teams design effective workshops and assessments for organizations to quickly clarify where they are, what they need, and what steps to take next. VantageSight can also provide workshop facilitation support and assessment results analysis, if needed, on a targeted basis.",
      leadIn: "Examples of workshops and assessments with which VantageSight can help include:",
      bullets: [
        "Organizational and workforce AI readiness assessments",
        "AI governance and compliance gap assessments",
        "AI strategy and governance workshops",
        "Executive briefings and targeted short sessions",
        "Independent quality reviews",
        "AI adoption planning sessions",
      ],
    },
  ];

  return (
    <section id="services" className="relative bg-bg-section py-20 md:py-28 overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent" />
      <div className="absolute top-1/3 -left-32 w-[300px] h-[300px] bg-primary/4 rounded-full blur-[100px]" />
      <div className="absolute bottom-1/4 -right-32 w-[300px] h-[300px] bg-primary/4 rounded-full blur-[100px]" />

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-5xl mx-auto text-center mb-14">
          <ScrollReveal>
            <h2 className="font-serif text-[36px] md:text-[48px] font-semibold text-text-headline mb-4">
              Advisory Services
            </h2>
            <div className="w-16 h-1 bg-primary mx-auto mb-6 rounded-full" />
            <p className="font-sans text-[17px] md:text-[19px] text-text-muted max-w-3xl mx-auto">
              Strategic guidance designed for organizations ready to move from scattered AI interest to structured, responsible, enterprise-ready AI adoption.
            </p>
          </ScrollReveal>
        </div>

        {/* Featured image - AI roadmap presentation */}
        <ScrollReveal delay={100}>
            <div className="max-w-4xl mx-auto mb-14">
              <div className="relative group rounded-2xl overflow-hidden shadow-2xl bg-bg-card">
                <img
                  src="https://static.readdy.ai/image/ff63a95a3d068db59710e28833339873/83f5a28cc4a80d2ab01f69cccf31753f.png"
                  alt="AI strategy advisor presenting roadmap and governance framework to leadership team in modern conference room"
                  className="w-full h-auto object-contain object-center"
                />
              </div>
            </div>
          </ScrollReveal>

        <div className="flex flex-col gap-8 max-w-5xl mx-auto">
          {services.map((s, i) => (
            <ScrollReveal key={i} delay={i * 100} direction="up">
              <div className={`bg-bg-card rounded-2xl p-8 md:p-10 border transition-all duration-300 hover:-translate-y-2 hover:border-primary/40 group relative overflow-hidden ${s.featured ? 'border-primary/30 ring-1 ring-primary/10' : 'border-border'}`}>
                {/* Hover glow effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                {s.featured && (
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary/50 via-primary to-primary/50" />
                )}
                <div className="relative z-10 grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
                  <div className="lg:col-span-2">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-5 transition-all duration-300 group-hover:scale-110 ${s.featured ? 'bg-primary/15' : 'bg-primary-tint'} group-hover:bg-primary/15`}>
                      <i className={`${s.icon} text-primary text-xl w-6 h-6 flex items-center justify-center`} />
                    </div>
                    <h3 className="font-sans text-[22px] md:text-[26px] font-semibold mb-3 text-text-headline group-hover:text-primary transition-colors duration-300">
                      {s.title}
                    </h3>
                    <p className="font-sans text-[15px] text-text-muted leading-relaxed">
                      {s.desc}
                    </p>
                  </div>

                  <div className="lg:col-span-3">
                    <p className="font-sans text-[15px] md:text-[16px] text-white leading-relaxed mb-3">
                      {s.leadIn}
                    </p>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {s.bullets.map((bullet, j) => (
                        <li key={j} className="flex items-start gap-2">
                          <i className="ri-check-line text-primary w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5" />
                          <span className="font-sans text-[14px] text-text-body leading-relaxed">
                            {bullet}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          ))}
        </div>

        <ScrollReveal delay={600}>
          <div className="text-center mt-14">
            <Link
              to="/contact"
              className="font-sans text-[16px] font-medium text-white bg-primary hover:bg-primary-light px-8 py-4 rounded-full transition-colors whitespace-nowrap cursor-pointer inline-block"
            >
              Talk Through Your Needs
            </Link>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}